import React from 'react';
import {
  AGENT_TOOL_BUDGET_USD,
  AGENT_TOOL_CAP_USD,
  AgentModelChoice,
  AgentToolSettings,
  getAgentToolSettings,
  putAgentToolSettings,
} from '../../api/settings';
import { SwitchField } from '../modals/ReviewerSettingsSection';
import { chatModelsForWriting, imageOption, loadChatModels, loadImageModels, useCatalog } from './agentToolsCatalog';
import CarouselMark from './CarouselMark';
import ModelMultiSelect from './ModelMultiSelect';

const draftNumber = (draft: string): number => (draft.trim() === '' ? NaN : Number(draft));
const sameRef = (a: AgentModelChoice, b: AgentModelChoice) => a.provider === b.provider && a.model === b.model;

const Help: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mt-1 text-[11px] text-[var(--app-text-muted)]">{children}</p>
);

/**
 * What the chat agent's own tool calls may use and spend: the image and writing
 * models, the two caps, the two paid searches and whether AI pictures are
 * labelled. One Save, and the server's
 * refusal is shown as it words it with the edits kept. It reads the saved values
 * first and offers nothing to save over them if it cannot: defaults passed off as
 * the user's own would spend. The locked image model is set under Generation; it
 * is always allowed and is only shown here.
 */
const AgentToolsPanel: React.FC = () => {
  const fieldId = React.useId();
  const images = useCatalog(loadImageModels);
  const chat = useCatalog(loadChatModels);
  const [served, setServed] = React.useState<AgentToolSettings | null>(null);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [imageModels, setImageModels] = React.useState<AgentModelChoice[]>([]);
  const [writingModels, setWritingModels] = React.useState<AgentModelChoice[]>([]);
  // The two numbers are typed, so they live as text until Save.
  const [capDraft, setCapDraft] = React.useState('');
  const [budgetDraft, setBudgetDraft] = React.useState('');
  const [xSearch, setXSearch] = React.useState(true);
  const [photoSearch, setPhotoSearch] = React.useState(true);
  const [aiLabels, setAiLabels] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState('');

  const show = React.useCallback((record: AgentToolSettings) => {
    // The locked model is always allowed: it is shown as such, never as a choice.
    setImageModels(record.image_models.filter((model) => !sameRef(model, record.locked_image_model)));
    setWritingModels(record.writing_models);
    setCapDraft(String(record.agent_cap_usd));
    setBudgetDraft(String(record.max_budget_usd));
    setXSearch(record.paid_x_search);
    setPhotoSearch(record.paid_photo_search);
    setAiLabels(record.ai_labels);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    getAgentToolSettings()
      .then((record) => {
        if (cancelled) return;
        setServed(record);
        show(record);
      })
      .catch(() => !cancelled && setLoadFailed(true));
    return () => {
      cancelled = true;
    };
  }, [show]);

  /** Any edit makes the last result stale. */
  const edit = <T,>(set: (value: T) => void) => (value: T) => {
    setSaved(false);
    setError('');
    set(value);
  };

  if (loadFailed) {
    return (
      <p role="alert" className="text-xs text-red-400">
        Couldn&apos;t load your agent tool settings. Close this and try again.
      </p>
    );
  }
  if (!served) return <p className="text-xs text-[var(--app-text-muted)]">Loading…</p>;

  const cap = draftNumber(capDraft);
  const budget = draftNumber(budgetDraft);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      // The section alone: the locked model and the carousel size are not ours to send.
      const record = await putAgentToolSettings({
        image_models: imageModels,
        writing_models: writingModels,
        agent_cap_usd: cap,
        max_budget_usd: budget,
        paid_x_search: xSearch,
        paid_photo_search: photoSearch,
        ai_labels: aiLabels,
      });
      show({ ...record, locked_image_model: served.locked_image_model });
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save the agent tool settings');
    } finally {
      setSaving(false);
    }
  };

  const { width, height } = served.carousel_size;
  return (
    <div className="flex max-w-xl flex-col gap-4" data-testid="agent-tools-panel">
      <ModelMultiSelect
        label="Image models"
        noun="image model"
        catalog={images.models.map(imageOption)}
        status={images.status}
        selected={imageModels}
        onChange={edit(setImageModels)}
        locked={served.locked_image_model}
        marks={(option) => <CarouselMark model={option.model} size={served.carousel_size} />}
      />
      <Help>
        The agent may draw with these, and always with your default image model. A model marked “carousel size” makes
        {` ${width} × ${height}`}, the carousel size set under Generation.
      </Help>

      <div>
        <ModelMultiSelect
          label="Writing models"
          noun="writing model"
          catalog={chatModelsForWriting(chat.models)}
          status={chat.status}
          selected={writingModels}
          onChange={edit(setWritingModels)}
        />
        <Help>The models its writing helper may use. None chosen means the chat&apos;s own model only.</Help>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${fieldId}-cap`} className="mb-1 block text-[10px] uppercase tracking-widest text-[var(--app-text-muted)]">
            Assistant cost cap per production (USD)
          </label>
          <input
            id={`${fieldId}-cap`}
            type="number"
            className="input w-full"
            min={AGENT_TOOL_CAP_USD.min}
            max={AGENT_TOOL_CAP_USD.max}
            step={AGENT_TOOL_CAP_USD.step}
            value={capDraft}
            onChange={(e) => edit(setCapDraft)(e.target.value)}
          />
          <Help>What the assistant&apos;s own model calls may cost in one production.</Help>
        </div>
        <div>
          <label htmlFor={`${fieldId}-budget`} className="mb-1 block text-[10px] uppercase tracking-widest text-[var(--app-text-muted)]">
            Largest budget per production (USD)
          </label>
          <input
            id={`${fieldId}-budget`}
            type="number"
            className="input w-full"
            min={AGENT_TOOL_BUDGET_USD.min}
            max={AGENT_TOOL_BUDGET_USD.max}
            step={AGENT_TOOL_BUDGET_USD.step}
            value={budgetDraft}
            onChange={(e) => edit(setBudgetDraft)(e.target.value)}
          />
          <Help>All approved budget cards of one production, margins included. A card that would go above it is refused.</Help>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <SwitchField label="Paid X search" checked={xSearch} onToggle={() => edit(setXSearch)(!xSearch)} />
        <SwitchField label="Paid photo search" checked={photoSearch} onToggle={() => edit(setPhotoSearch)(!photoSearch)} />
        <Help>Switched off, the agent cannot use that search at all.</Help>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)]">Labels</h3>
        <SwitchField label="AI labels" checked={aiLabels} onToggle={() => edit(setAiLabels)(!aiLabels)} />
        <Help>Show an AI label on content that uses AI pictures, and say so in the credits.</Help>
      </div>

      {error && (
        <p role="alert" className="text-xs text-red-400">
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-3">
        {saved && (
          <span role="status" className="text-xs text-[var(--app-text-muted)]">
            Saved.
          </span>
        )}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={saving || !Number.isFinite(cap) || !Number.isFinite(budget)}
          onClick={() => void save()}
        >
          {saving ? 'Saving...' : 'Save agent tools'}
        </button>
      </div>
    </div>
  );
};

export default AgentToolsPanel;
